import React, { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Divider,
  Box,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const tokenPreview = (token) => {
  if (!token) return "—";
  if (token.length <= 18) return token;
  return `${token.slice(0, 10)}…${token.slice(-6)}`;
};

export default function RegLinksTable({
  t,
  selectedTenant,
  links,
  loading,
  includeInactive,
  setIncludeInactive,
  onInvalidate,
  onRefresh,
  onCopy,
}) {
  const [invalidatingToken, setInvalidatingToken] = useState("");

  // ✅ If includeInactive is OFF -> show only usable (active + usable)
  // ✅ If includeInactive is ON  -> show all rows
  const rows = useMemo(() => {
    const list = Array.isArray(links) ? links : [];
    if (includeInactive) return list;
    return list.filter((x) => x?.is_active && x?.is_usable);
  }, [links, includeInactive]);

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="subtitle1">
          {t("SuperAdminTenantsRegLinksPage.links.title", "Registračné odkazy")}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                disabled={!selectedTenant}
              />
            }
            label={t(
              "SuperAdminTenantsRegLinksPage.links.includeInactive",
              "Zobraziť aj neaktívne"
            )}
          />
          <Button variant="outlined" onClick={onRefresh} disabled={!selectedTenant || loading}>
            {t("SuperAdminTenantsRegLinksPage.actions.refresh", "Obnoviť")}
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {!selectedTenant ? (
        <Typography variant="body2" color="text.secondary">
          {t(
            "SuperAdminTenantsRegLinksPage.links.selectTenant",
            "Vyberte tenant, aby ste videli jeho registračné odkazy."
          )}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("SuperAdminTenantsRegLinksPage.table.token", "Token")}
                </TableCell>
                <TableCell>
                  {t("SuperAdminTenantsRegLinksPage.table.suffix", "Prípona")}
                </TableCell>
                <TableCell>
                  {t("SuperAdminTenantsRegLinksPage.table.created", "Vytvorený")}
                </TableCell>
                <TableCell>
                  {t("SuperAdminTenantsRegLinksPage.table.expires", "Platný do")}
                </TableCell>
                <TableCell>
                  {t("SuperAdminTenantsRegLinksPage.table.uses", "Použitia")}
                </TableCell>
                <TableCell>
                  {t("SuperAdminTenantsRegLinksPage.table.status", "Stav")}
                </TableCell>
                <TableCell align="right">
                  {t("SuperAdminTenantsRegLinksPage.table.actions", "Akcie")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        {t("SuperAdminTenantsRegLinksPage.links.loading", "Načítavam odkazy…")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      {includeInactive
                        ? t(
                            "SuperAdminTenantsRegLinksPage.links.emptyAll",
                            "Nenašli sa žiadne odkazy."
                          )
                        : t(
                            "SuperAdminTenantsRegLinksPage.links.emptyUsable",
                            "Nenašli sa žiadne použiteľné odkazy. Skúste zapnúť „Zobraziť aj neaktívne“."
                          )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => {
                  const used = row.used_count ?? 0;
                  const max = row.max_uses;
                  const usesText = max == null ? `${used} / ∞` : `${used} / ${max}`;

                  const active = !!row.is_active;
                  const usable = !!row.is_usable;

                  let statusChip = (
                    <Chip
                      label={t("SuperAdminTenantsRegLinksPage.status.usable", "použiteľný")}
                      size="small"
                    />
                  );
                  if (!active) {
                    statusChip = (
                      <Chip
                        label={t("SuperAdminTenantsRegLinksPage.status.inactive", "neaktívny")}
                        size="small"
                      />
                    );
                  } else if (!usable) {
                    statusChip = (
                      <Chip
                        label={t("SuperAdminTenantsRegLinksPage.status.notUsable", "nepoužiteľný")}
                        size="small"
                      />
                    );
                  }

                  const token = row.token;
                  const busy = invalidatingToken === token;

                  return (
                    <TableRow key={row.id ?? token} hover>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        <Tooltip title={token || ""}>
                          <span>{tokenPreview(token)}</span>
                        </Tooltip>
                      </TableCell>

                      <TableCell>{row.label_suffix || "—"}</TableCell>

                      <TableCell>{formatDate(row.created_at)}</TableCell>
                      <TableCell>{formatDate(row.expires_at)}</TableCell>

                      <TableCell>{usesText}</TableCell>

                      <TableCell>{statusChip}</TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              onCopy(token, "SuperAdminTenantsRegLinksPage.snackbar.tokenCopied")
                            }
                            disabled={!token}
                          >
                            {t("SuperAdminTenantsRegLinksPage.actions.copy", "Kopírovať")}
                          </Button>

                          <Button
                            size="small"
                            color="error"
                            variant="contained"
                            disabled={!active || busy || !token}
                            onClick={async () => {
                              setInvalidatingToken(token);
                              try {
                                await onInvalidate(token);
                              } finally {
                                setInvalidatingToken("");
                              }
                            }}
                          >
                            {busy
                              ? "…"
                              : t("SuperAdminTenantsRegLinksPage.actions.invalidate", "Zneplatniť")}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
